import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AuthAgency, AuthSession, AuthUser, LoginApiPayload, LoginApiResponse, LoginRequest } from './auth.types';
import { Role } from './role.enum';
import { environment } from '../../../environments/environment';
import type { User } from '../models/user.model';
import * as CryptoJS from 'crypto-js';
import { LOADER_MESSAGE } from '../interceptors/loader.interceptor';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private static readonly SESSION_STORAGE_KEY = 'underwriter-intelligence-auth-session';
  private static readonly LOGIN_ENDPOINT = `${environment.futBaseAdminopUrl}userlogin`;
  private static readonly REFRESH_TOKEN_ENDPOINT = `${environment.futBaseAdminopUrl}get-refresh-token`;
  private static readonly SESSION_EXPIRY_WARNING_MS = 5 * 60 * 1000;
  private static readonly DEFAULT_SESSION_DURATION_MS = 179 * 60 * 1000;
  private static readonly DECRYPTION_KEY = CryptoJS.enc.Utf8.parse(environment.Key);
  private static readonly DECRYPTION_IV = CryptoJS.enc.Utf8.parse(environment.Iv);

  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly sessionState = signal<AuthSession | null>(this.restoreSession());
  private readonly clock = signal(Date.now());

  readonly session = this.sessionState.asReadonly();
  readonly isAuthenticated = computed(() => this.sessionState() !== null);
  readonly role = computed(() => this.sessionState()?.role ?? null);
  readonly displayRole = computed(() => this.sessionState()?.userType ?? this.sessionState()?.role ?? null);
  readonly userType = computed(() => this.sessionState()?.userType ?? null);
  readonly email = computed(() => this.sessionState()?.email ?? '');
  readonly userId = computed(() => this.sessionState()?.userId ?? this.sessionState()?.user?.user_id ?? null);
  readonly accessToken = computed(() => this.sessionState()?.accessToken ?? '');
  readonly currentUser = computed<User>(() => this.buildCurrentUser());
  readonly sessionExpiry = computed(() => {
    const session = this.sessionState();
    const expiresAt = session?.expiresAt ?? null;

    if (!expiresAt) {
      return null;
    }

    const remainingMs = expiresAt - this.clock();
    const safeRemainingMs = Math.max(remainingMs, 0);

    return {
      expiresAt,
      remainingMs: safeRemainingMs,
      expired: remainingMs <= 0,
      warning: remainingMs <= AuthService.SESSION_EXPIRY_WARNING_MS,
      label: this.formatRemainingTime(safeRemainingMs),
      message: remainingMs <= 0
        ? 'Your session has expired. Please sign in again.'
        : `Your session expires in ${this.formatRemainingTime(safeRemainingMs)}. Save your work now.`
    };
  });

  constructor() {
    effect((onCleanup) => {
      const session = this.sessionState();

      if (!session?.expiresAt) {
        this.clock.set(Date.now());
        return;
      }

      this.clock.set(Date.now());

      const intervalId = globalThis.setInterval(() => {
        this.clock.set(Date.now());
      }, 1000);

      const timeoutId = globalThis.setTimeout(() => {
        this.expireSession();
      }, Math.max(0, session.expiresAt - Date.now()));

      onCleanup(() => {
        globalThis.clearInterval(intervalId);
        globalThis.clearTimeout(timeoutId);
      });
    });
  }

  login(request: LoginRequest): Observable<AuthSession> {
    // debugger
    const payload: LoginApiPayload = {
      user_id: request.userId.trim(),
      password: request.password
    };

    return this.http.post<LoginApiResponse>(AuthService.LOGIN_ENDPOINT, payload, {
      context: new HttpContext().set(LOADER_MESSAGE, 'Signing in'),
    }).pipe(
      // tap((response) => console.log('login response', response)),
      map((response) => this.mapSession(response, request)),
      tap((session) => {
        this.sessionState.set(session);
        this.persistSession(session);
      })
    );
  }

  refreshSessionToken(): Observable<AuthSession> {
    const currentSession = this.sessionState();

    if (!currentSession?.accessToken) {
      return throwError(() => new Error('No active session found.'));
    }

    return this.http.get<unknown>(AuthService.REFRESH_TOKEN_ENDPOINT, {
      context: new HttpContext().set(LOADER_MESSAGE, 'Refreshing session'),
      headers: new HttpHeaders({
        Authorization: `Bearer ${currentSession.accessToken}`
      })
    }).pipe(
      map((response) => {
        const refreshedAccessToken = this.extractTokenValue(response);

        if (!refreshedAccessToken) {
          throw new Error('Refresh token response did not include a token.');
        }

        const refreshedExpiresAt = this.extractRefreshTokenExpiry(response, refreshedAccessToken);
        const nextSession: AuthSession = {
          ...currentSession,
          accessToken: refreshedAccessToken,
          expiresAt: refreshedExpiresAt ?? (Date.now() + AuthService.DEFAULT_SESSION_DURATION_MS)
        };

        this.sessionState.set(nextSession);
        this.persistSession(nextSession);

        return nextSession;
      })
    );
  }

  logout(options?: { redirectToLogin?: boolean; reason?: string }): void {
    this.sessionState.set(null);
    this.clearSession();

    if (options?.redirectToLogin) {
      void this.router.navigateByUrl(
        this.router.createUrlTree(['/authentication/login'], {
          queryParams: options.reason ? { reason: options.reason } : undefined
        }),
        { replaceUrl: true }
      );
    }
  }

  hasAnyRole(roles: readonly Role[]): boolean {
    const currentRole = this.role();
    return currentRole !== null && roles.includes(currentRole);
  }

  hasAnyUserType(userTypes: readonly string[]): boolean {
    const currentUserType = this.userType()?.trim().toUpperCase();

    return currentUserType !== null && userTypes.some((userType) => userType.trim().toUpperCase() === currentUserType);
  }

  getDefaultRouteForRole(role = this.role()): string {
    switch (role) {
      case Role.Admin:
      case Role.Underwriter:
        return '/workbench';
      case Role.Client:
      case Role.User:
      default:
        return '/workbench';
    }
  }

  hasExpiredSession(): boolean {
    const expiresAt = this.sessionState()?.expiresAt;
    return typeof expiresAt === 'number' && expiresAt <= Date.now();
  }

  private mapSession(response: LoginApiResponse, request: LoginRequest): AuthSession {
    const accessToken = this.extractTokenValue(response);
    const refreshToken = this.extractTokenValue(response, ['refresh_token']);
    const responseStatus = this.extractResponseField(response, 'status');
    const errorMessage =
      this.extractResponseFieldAsString(response, 'message')
      ?? this.extractResponseFieldAsString(response, 'msg')
      ?? 'Login failed. Please check your credentials.';

    if (Number(responseStatus) !== 1 || !accessToken) {
      throw new Error(errorMessage);
    }

    const user = this.parseApiValue<AuthUser>(this.extractResponseField(response, 'user'));
    const agency = this.parseApiValue<AuthAgency>(this.extractResponseField(response, 'agency'));
    const additionalAccess = this.parseApiValue<unknown>(this.extractResponseField(response, 'additional_access'));
    const userType = user?.usertype?.trim();
    const role = userType ? this.mapUserTypeToRole(userType) : Role.Underwriter;

    return {
      email: request.userId.trim().toLowerCase(),
      role,
      userType,
      userId: this.parseUserId(this.extractResponseField(response, 'user_id')) ?? user?.user_id,
      accessToken,
      refreshToken: refreshToken ?? undefined,
      expiresAt: this.resolveSessionExpiry(response, accessToken) ?? (Date.now() + AuthService.DEFAULT_SESSION_DURATION_MS),
      user,
      agency,
      additionalAccess,
    };
  }

  private buildCurrentUser(): User {
    const session = this.sessionState();
    const user = session?.user;
    const fullName = [user?.first_name, user?.last_name].filter((part) => Boolean(part?.trim())).join(' ').trim();
    const name = fullName || this.fallbackName(session?.email ?? '');
    const email = user?.email?.trim() || session?.email?.trim() || '';
    const role = session?.userType?.trim() || session?.role || Role.User;

    return {
      id: String(session?.userId ?? user?.user_id ?? (email || 'session-user')),
      name,
      initials: this.buildInitials(name, email),
      role,
      email,
    };
  }

  private fallbackName(email: string): string {
    const localPart = email.split('@')[0]?.trim();

    if (!localPart) {
      return 'Signed-in user';
    }

    return localPart
      .split(/[._-]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private buildInitials(name: string, email: string): string {
    const source = name.trim() || email.trim();
    const parts = source.split(/\s+/).filter(Boolean);

    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    }

    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }

    return 'SU';
  }

  private resolveSessionExpiry(response: LoginApiResponse, token?: string): number | null {
    const directExpiry = this.parseAbsoluteExpiryValue(
      this.extractResponseField(response, 'expires_at')
      ?? this.extractResponseField(response, 'expiry')
      ?? this.extractResponseField(response, 'token_expiry')
    );

    if (directExpiry !== null) {
      return directExpiry;
    }

    const expiresIn = this.parseExpiryValue(this.extractResponseField(response, 'expires_in'));
    if (expiresIn !== null) {
      return Date.now() + expiresIn * 1000;
    }

    const tokenExpiry = this.extractJwtExpiry(token ?? this.extractResponseFieldAsString(response, 'access_token'));
    return tokenExpiry;
  }

  private parseUserId(value: unknown): number | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    }

    return undefined;
  }

  private persistSession(session: AuthSession): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    try {
      sessionStorage.setItem(AuthService.SESSION_STORAGE_KEY, JSON.stringify(session));
    } catch {
      // Ignore storage failures and keep the in-memory session working for the current tab.
    }
  }

  private restoreSession(): AuthSession | null {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }

    try {
      const rawSession = sessionStorage.getItem(AuthService.SESSION_STORAGE_KEY);

      if (!rawSession) {
        return null;
      }

      const parsed = JSON.parse(rawSession) as AuthSession;

      if (!parsed?.accessToken || !parsed?.role || !parsed?.email) {
        return null;
      }

      const hasStoredExpiry = Object.prototype.hasOwnProperty.call(parsed, 'expiresAt');
      if (!hasStoredExpiry || parsed.expiresAt === undefined) {
        return null;
      }

      const expiresAt = this.parseAbsoluteExpiryValue(parsed.expiresAt);
      if (expiresAt === null) {
        return null;
      }

      return {
        ...parsed,
        expiresAt: expiresAt ?? undefined
      };
    } catch {
      return null;
    }
  }

  private clearSession(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    try {
      sessionStorage.removeItem(AuthService.SESSION_STORAGE_KEY);
    } catch {
      // Ignore storage failures.
    }
  }

  private expireSession(): void {
    if (!this.sessionState()) {
      return;
    }

    this.logout({ redirectToLogin: true, reason: 'session-expired' });
  }

  private parseExpiryValue(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value > 0 ? value : null;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();

      if (!trimmed) {
        return null;
      }

      const numeric = Number(trimmed);
      if (Number.isFinite(numeric)) {
        return numeric > 0 ? numeric : null;
      }

      const parsedDate = Date.parse(trimmed);
      return Number.isFinite(parsedDate) ? parsedDate : null;
    }

    return null;
  }

  private parseAbsoluteExpiryValue(value: unknown): number | null {
    const parsedValue = this.parseExpiryValue(value);

    if (parsedValue === null) {
      return null;
    }

    return parsedValue < 10_000_000_000 ? parsedValue * 1000 : parsedValue;
  }

  private unwrapApiResponse<T>(response: T): T {
    if (!response || typeof response !== 'object') {
      return response;
    }

    const record = response as Record<string, unknown>;

    if (record['data'] && typeof record['data'] === 'object') {
      return this.unwrapApiResponse(record['data'] as T);
    }

    if (record['result'] && typeof record['result'] === 'object') {
      return this.unwrapApiResponse(record['result'] as T);
    }

    return response;
  }

  private extractResponseField(response: unknown, key: string): unknown {
    if (!response || typeof response !== 'object') {
      return undefined;
    }

    const record = response as Record<string, unknown>;
    const directValue = record[key];

    if (directValue !== undefined) {
      return directValue;
    }

    if (record['data'] && typeof record['data'] === 'object') {
      const nestedValue = this.extractResponseField(record['data'], key);
      if (nestedValue !== undefined) {
        return nestedValue;
      }
    }

    if (record['result'] && typeof record['result'] === 'object') {
      const nestedValue = this.extractResponseField(record['result'], key);
      if (nestedValue !== undefined) {
        return nestedValue;
      }
    }

    return undefined;
  }

  private extractResponseFieldAsString(response: unknown, key: string): string | undefined {
    const value = this.extractResponseField(response, key);

    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed || undefined;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }

    return undefined;
  }

  private extractTokenValue(response: unknown, keys: readonly string[] = ['access_token', 'token', 'refresh_token']): string | null {
    if (typeof response === 'string') {
      const trimmed = response.trim();
      return trimmed ? trimmed : null;
    }

    if (!response || typeof response !== 'object') {
      return null;
    }

    const record = response as Record<string, unknown>;

    for (const key of keys) {
      const candidate = record[key];

      if (typeof candidate === 'string') {
        const trimmed = candidate.trim();
        if (trimmed) {
          return trimmed;
        }
      }
    }

    if (record['data'] && typeof record['data'] === 'object') {
      const nestedData = this.extractTokenValue(record['data'], keys);
      if (nestedData) {
        return nestedData;
      }
    }

    if (record['result'] && typeof record['result'] === 'object') {
      const nestedResult = this.extractTokenValue(record['result'], keys);
      if (nestedResult) {
        return nestedResult;
      }
    }

    return null;
  }

  private extractRefreshTokenExpiry(response: unknown, token: string): number | null {
    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const absoluteExpiry = this.parseAbsoluteExpiryValue(
        record['expires_at']
        ?? record['expiry']
        ?? record['token_expiry']
      );

      if (absoluteExpiry !== null) {
        return absoluteExpiry;
      }

      const expiresIn = this.parseExpiryValue(record['expires_in']);

      if (expiresIn !== null) {
        return Date.now() + expiresIn * 1000;
      }
    }

    const tokenExpiry = this.extractJwtExpiry(token);
    return tokenExpiry;
  }

  private extractJwtExpiry(token?: string): number | null {
    if (!token) {
      return null;
    }

    const segments = token.split('.');

    if (segments.length !== 3) {
      return null;
    }

    try {
      const payload = JSON.parse(this.base64UrlDecode(segments[1])) as { exp?: unknown };
      const exp = this.parseExpiryValue(payload.exp);

      return exp !== null && exp < 10_000_000_000 ? exp * 1000 : exp;
    } catch {
      return null;
    }
  }

  private base64UrlDecode(value: string): string {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    return atob(padded);
  }

  private formatRemainingTime(remainingMs: number): string {
    const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${String(minutes).padStart(2, '0')}m`;
    }

    if (minutes > 0) {
      return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
    }

    return `${seconds}s`;
  }

  private mapUserTypeToRole(userType: string | undefined): Role {
    const normalizedUserType = userType?.trim().toUpperCase();

    if (!normalizedUserType) {
      throw new Error('User type is missing from the login response.');
    }

    switch (normalizedUserType) {
      case 'UNDERWRITER':
      case 'UNDERWRITING MANAGER':
        return Role.Underwriter;
      case 'UNDERWRITER ASSISTANT':
        return Role.UnderwriterAssistant;
      case 'OPERATIONS MANAGER':
        return Role.SuperAdmin;
      case 'ADMIN (ALL)':
      case 'ADMIN (CLAIMS)':
      case 'BROKER ADMIN':
      case 'VIEWER (POLICY)':
      case 'ADMIN (POLICY)':
      case 'IT USER (ALL)':
      case 'GENERAL USER':
      case 'CSR (POLICY)':
        return Role.Admin;
      case 'BROKER USER':
        return Role.User;
      default:
        // Unknown user types still get the least-privileged app role and are routed by the guards.
        return Role.User;
      // throw new Error(userType ? `Unsupported user type: ${userType}` : 'User type is missing from the login response.');

    }
  }

  private decryptValue(value?: string): string | undefined {
    if (!value) {
      return undefined;
    }

    try {
      const decrypted = CryptoJS.AES.decrypt(value, AuthService.DECRYPTION_KEY, {
        iv: AuthService.DECRYPTION_IV,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }).toString(CryptoJS.enc.Utf8).trim();

      return decrypted || value;
    } catch {
      return value;
    }
  }

  private parseApiValue<T>(value: unknown): T | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }

    if (typeof value === 'object') {
      return value as T;
    }

    if (typeof value !== 'string') {
      return undefined;
    }

    const decrypted = this.decryptValue(value);

    if (!decrypted) {
      return undefined;
    }

    try {
      return JSON.parse(decrypted) as T;
    } catch {
      return undefined;
    }
  }

}
